import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Trash2, LogOut } from "lucide-react";
import { Logo } from "../components/landing/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS = { nove: "Nové", v_reseni: "V řešení", vyrizeno: "Vyřízeno" };
const inputCls = "w-full bg-transparent border-b border-[var(--line)] py-3 text-[var(--ink)] focus:outline-none focus:border-[var(--cyan)]";

const errText = (e) => {
  const d = e?.response?.data?.detail;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg).join(" ");
  return "Něco se pokazilo.";
};

const Login = ({ onToken }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password });
      onToken(data.token);
    } catch (e2) { setErr(errText(e2)); }
  };
  return (
    <form data-testid="admin-login-form" onSubmit={submit} className="hud-frame w-full max-w-md border border-[var(--line)] bg-[var(--surface)] p-8 space-y-6">
      <h1 className="font-display text-2xl uppercase">Správa poptávek</h1>
      <input data-testid="admin-email" type="email" required placeholder="E-mail" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
      <input data-testid="admin-password" type="password" required placeholder="Heslo" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} />
      {err && <p data-testid="admin-login-error" className="text-red-700 text-sm">{err}</p>}
      <button data-testid="admin-login-submit" className="w-full rounded-full bg-[var(--cyan)] text-white py-3 font-semibold">Přihlásit</button>
    </form>
  );
};

const Row = ({ q, onStatus, onDelete }) => (
  <div data-testid={`inquiry-row-${q.id}`} className="border border-[var(--line)] bg-[var(--surface)] p-5 md:p-6 grid md:grid-cols-12 gap-4">
    <div className="md:col-span-4">
      <p className="font-display text-lg">{q.name}</p>
      <p className="text-sm text-[var(--muted)] break-all">{q.email}</p>
      <p className="text-sm text-[var(--muted)]">{q.phone || "—"}</p>
    </div>
    <div className="md:col-span-5 text-sm space-y-1">
      <p><span className="text-[var(--cyan)] font-mono text-xs uppercase tracking-widest">{q.event_type}</span> · {q.event_date || "datum neuvedeno"} · {q.guests ?? "?"} hostů</p>
      {Array.isArray(q.dishes) && q.dishes.length > 0 && (
        <p data-testid={`inquiry-dishes-${q.id}`}><span className="font-mono text-xs uppercase tracking-widest text-[var(--muted)]">Jídla: </span>{q.dishes.join(", ")}</p>
      )}
      <p className="text-[var(--ink)] whitespace-pre-line">{q.message || "—"}</p>
      <p className="font-mono text-xs text-[var(--muted)]">{new Date(q.created_at).toLocaleString("cs-CZ")}</p>
    </div>
    <div className="md:col-span-3 flex md:flex-col md:items-end gap-3">
      <select data-testid={`inquiry-status-${q.id}`} value={q.status} onChange={(e) => onStatus(q.id, e.target.value)} className="bg-white border border-[var(--line)] rounded-full px-4 py-2 text-sm">
        {Object.entries(STATUS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
      </select>
      <button data-testid={`inquiry-delete-${q.id}`} onClick={() => onDelete(q.id)} className="text-red-700 hover:text-red-900 flex items-center gap-2 text-sm"><Trash2 size={16} /> Smazat</button>
    </div>
  </div>
);

export default function Admin() {
  const [token, setToken] = useState(() => sessionStorage.getItem("jjs_token") || "");
  const [items, setItems] = useState([]);
  const headers = { Authorization: `Bearer ${token}` };

  const logout = () => { sessionStorage.removeItem("jjs_token"); setToken(""); };
  const load = useCallback(async () => {
    if (!token) return;
    try {
      const { data } = await axios.get(`${API}/inquiries`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(Array.isArray(data) ? data : []);
    } catch (e) { if (e?.response?.status === 401) { sessionStorage.removeItem("jjs_token"); setToken(""); } }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const onToken = (t) => { sessionStorage.setItem("jjs_token", t); setToken(t); };
  const onStatus = async (id, status) => {
    try { await axios.patch(`${API}/inquiries/${id}`, { status }, { headers }); toast.success("Stav uložen"); load(); }
    catch (e) { toast.error(errText(e)); }
  };
  const onDelete = async (id) => {
    if (!window.confirm("Opravdu smazat poptávku?")) return;
    try { await axios.delete(`${API}/inquiries/${id}`, { headers }); load(); }
    catch (e) { toast.error(errText(e)); }
  };

  return (
    <div data-testid="admin-page" className="min-h-screen grid-bg">
      <header className="border-b border-[var(--line)] px-5 md:px-10 h-[68px] flex items-center justify-between">
        <Logo testId="admin-logo" />
        {token && <button data-testid="admin-logout" onClick={logout} className="flex items-center gap-2 text-sm text-[var(--muted)] hover:text-[var(--ink)]"><LogOut size={16} /> Odhlásit</button>}
      </header>
      {!token ? (
        <div className="grid place-items-center px-5 py-24"><Login onToken={onToken} /></div>
      ) : (
        <main className="max-w-[1200px] mx-auto px-5 md:px-10 py-12">
          <h1 className="font-display text-3xl md:text-4xl uppercase">Poptávky <span className="text-[var(--cyan)]">{items.length}</span></h1>
          <div data-testid="inquiry-list" className="mt-8 space-y-4">
            {items.length === 0 && <p className="text-[var(--muted)]">Zatím žádné poptávky.</p>}
            {items.map((q) => <Row key={q.id} q={q} onStatus={onStatus} onDelete={onDelete} />)}
          </div>
        </main>
      )}
    </div>
  );
}
